import { useDrag, useDrop } from 'react-dnd';
import { useDnD } from '../components/DnD/DnDContext';

/**
 * Custom hook for draggable items
 * @param {string} type - The type of draggable item (e.g., 'section', 'chapter', 'lecture')
 * @param {object} item - The item data
 * @param {number} index - The item's position in the list
 * @param {function} onDragEnd - Optional callback when drag ends
 */
export const useDraggableItem = (type, item, index, onDragEnd = null) => {
    const { setIsDragging } = useDnD();

    const [{ isDragging }, drag, preview] = useDrag({
        type,
        item: () => {
            setIsDragging(true);
            return {
                type,
                id: item.id,
                index,
                data: item
            };
        },
        end: (draggedItem, monitor) => {
            setIsDragging(false);
            if (onDragEnd) {
                onDragEnd(draggedItem, monitor);
            }
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        }),
        canDrag: () => true,
    });

    return { isDragging, drag, preview };
};

/**
 * Custom hook for drop targets
 * @param {string|array} acceptTypes
 * @param {function} onDrop - Callback function: (item, targetIndex) => void
 * @param {function} onHover 
 * @param {string|number} targetItemId - The ID of the target item (returned in drop result)
 * @param {number} targetIndex - The index of the target item (passed to onDrop callback)
 */
export const useDropTarget = (acceptTypes, onDrop, onHover = null, targetItemId = null, targetIndex = null) => {
    const { setDragOverItem } = useDnD();

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: acceptTypes,
        drop: (item, monitor) => {
            if (onDrop && monitor.isOver({ shallow: true })) {
                // Pass the targetIndex to onDrop callback, or extract from drop result
                const finalTargetIndex = targetIndex !== null ? targetIndex : monitor.getDropResult()?.index;
                onDrop(item, finalTargetIndex);
            }
            setDragOverItem(null);
            // Return target item ID in drop result for onDragEnd handlers
            return targetItemId ? { id: targetItemId, index: targetIndex } : undefined;
        },
        hover: (item, monitor) => {
            if (onHover && monitor.isOver({ shallow: true })) {
                onHover(item, monitor);
            }
            if (monitor.isOver({ shallow: true })) {
                setDragOverItem(item);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop()
        })
    });

    return { isOver, canDrop, drop };
};

/**
 * Custom hook for reordering items within the same container using position switching
 * @param {string} entityType - The type of entity being reordered
 * @param {string} scopeType - The scope type (e.g., 'course', 'chapter')
 * @param {string} scopeId - The scope ID
 */
export const useReorder = (entityType, defaultScopeType = null, defaultScopeId = null) => {
    const { handleSwitchPositions } = useDnD();

    const reorder = async (draggedItem, targetIndex, scopeType = null, scopeId = null, itemsArray = null) => {
        // Validate targetIndex is a number
        if (typeof targetIndex !== 'number' || isNaN(targetIndex)) {
            console.error('Invalid targetIndex:', targetIndex, 'Expected a number');
            return { success: false, error: 'Invalid target index' };
        }

        if (draggedItem.index === targetIndex) {
            return; // No change needed
        }

        // Use provided scope parameters or fall back to defaults
        const finalScopeType = scopeType || defaultScopeType;
        const finalScopeId = scopeId || defaultScopeId;

        // For switching, we need the target item's ID
        // If itemsArray is provided, get the target item from it
        let targetItemId = null;
        
        if (itemsArray && Array.isArray(itemsArray) && itemsArray.length > 0) {
            // Ensure targetIndex is within bounds
            if (targetIndex >= 0 && targetIndex < itemsArray.length) {
                const targetItem = itemsArray[targetIndex];
                if (targetItem && targetItem.id !== undefined && targetItem.id !== null) {
                    // Ensure ID is a valid value (string or number)
                    targetItemId = String(targetItem.id);
                }
            }
        }
        
        // Validate that we have both IDs before switching
        if (!targetItemId || !draggedItem.id) {
            // If we can't get target item ID, fall back to old reorder method
            console.warn('Cannot switch positions: target item ID not available. Falling back to reorder.', {
                targetItemId,
                draggedItemId: draggedItem.id,
                targetIndex,
                itemsArrayLength: itemsArray?.length,
                itemsArrayType: Array.isArray(itemsArray)
            });
            // Note: handleReorder expects switchWithId, not newPosition
            // For courses without itemsArray, we need to use switchWithId approach
            // But we don't have the target item ID, so we can't switch
            // This should not happen if itemsArray is provided
            return { 
                success: false, 
                error: 'Cannot reorder: target item ID not available. Please ensure itemsArray is provided.' 
            };
        }

        // Ensure draggedItem.id is also a string for consistency
        const draggedItemId = String(draggedItem.id);

        // Don't switch if it's the same item
        if (draggedItemId === targetItemId) {
            console.log('Same item, no switch needed');
            return { success: true, message: 'No change needed' };
        }

        // Switch positions between dragged item and target item
        console.log('Switching positions:', {
            entityType,
            firstEntityId: draggedItemId,
            secondEntityId: targetItemId,
            scopeType: finalScopeType,
            scopeId: finalScopeId
        });

        return await handleSwitchPositions(
            entityType,
            draggedItemId,
            targetItemId,
            finalScopeType,
            finalScopeId
        );
    };

    return { reorder };
};

/**
 * Custom hook for transferring items between containers
 * @param {string} entityType - The type of entity being transferred
 */
export const useTransfer = (entityType) => {
    const { handleTransfer } = useDnD();

    const transfer = async (draggedItem, targetType, targetId) => {
        return await handleTransfer(
            entityType,
            draggedItem.id,
            targetType,
            targetId
        );
    };

    return { transfer };
};
